/**
 * CartCheckoutModal Component
 *
 * Modal that handles the full cart checkout flow:
 * 1. Confirmation stage - Review cart items and total cost
 * 2. Purchasing stage - All payloads sent to Xaman, live status list
 * 3. Complete stage - Summary of results
 */

import React from 'react';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { Progress } from '../../ui/components/Progress';
import {
  FeatherX,
  FeatherCheck,
  FeatherAlertCircle,
  FeatherShoppingCart,
  FeatherClock,
  FeatherLoader,
} from '@subframe/core';
import type { CartItem } from '../../store/cartStore';
import type {
  CartCheckoutState,
  CartPurchaseFailure,
  CheckoutItemState,
} from '../../hooks/useCartCheckout';
import type { CartTotals } from '../../utils/cartTotals';
import { is3DModelUrl } from '../../utils/media';
import SigningStepMonitor from '../trades/SigningStepMonitor';

type ResolveClientItem = (
  tokenId: string,
  status: 'signed' | 'rejected' | 'expired' | 'error',
  error?: string,
) => void;

interface CartCheckoutModalProps {
  open: boolean;
  onClose: () => void;
  items: CartItem[];
  state: CartCheckoutState;
  totals: CartTotals;
  onStartPurchase: () => void;
  onCancelConfirmation: () => void;
  onCancelRemaining: () => void;
  /** Resolve a single client-signing (joey-wc) item after its monitor settles. */
  onResolveClientItem: ResolveClientItem;
  onComplete: () => void;
}

export function CartCheckoutModal({
  open,
  onClose,
  items,
  state,
  totals,
  onStartPurchase,
  onCancelConfirmation,
  onCancelRemaining,
  onResolveClientItem,
  onComplete,
}: CartCheckoutModalProps) {
  const { stage, checkoutItems, completedPurchases, failedPurchases, isClientSigning } = state;

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Content className="max-w-lg w-full p-0 mobile:max-w-full mobile:m-4 !gap-0 !items-stretch">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-solid border-neutral-border">
          <div className="flex items-center gap-2">
            <FeatherShoppingCart className="h-5 w-5 text-brand-600" />
            <h2 className="text-heading-3 font-heading-3 text-default-font">
              {stage === 'confirming' && 'Confirm Cart Purchase'}
              {stage === 'purchasing' && 'Purchasing NFTs'}
              {stage === 'complete' && 'Checkout Complete'}
            </h2>
          </div>
          {stage !== 'purchasing' && (
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-neutral-100 transition-colors"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-4 mobile:p-3">
          {stage === 'confirming' && (
            <ConfirmationContent
              items={items}
              totals={totals}
              onStartPurchase={onStartPurchase}
              onCancel={onCancelConfirmation}
            />
          )}

          {stage === 'purchasing' && (
            <PurchasingContent
              checkoutItems={checkoutItems}
              isClientSigning={isClientSigning}
              onCancelRemaining={onCancelRemaining}
              onResolveClientItem={onResolveClientItem}
            />
          )}

          {stage === 'complete' && (
            <CompleteContent
              completedPurchases={completedPurchases}
              failedPurchases={failedPurchases}
              onComplete={onComplete}
            />
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
}

// ============================================================================
// Confirmation Content
// ============================================================================

interface ConfirmationContentProps {
  items: CartItem[];
  totals: CartTotals;
  onStartPurchase: () => void;
  onCancel: () => void;
}

function ConfirmationContent({ items, totals, onStartPurchase, onCancel }: ConfirmationContentProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="p-3 bg-brand-50 rounded-md">
        <p className="text-body font-body text-brand-700">
          You will sign {items.length} transaction{items.length !== 1 ? 's' : ''} in Xaman to complete this purchase.
          Each NFT requires a separate signature.
        </p>
      </div>

      <div className="max-h-[250px] overflow-y-auto border border-solid border-neutral-border rounded-md mobile:max-h-[200px]">
        {items.map((item, index) => (
          <div
            key={item.nft_token_id}
            className={`flex items-center gap-3 p-3 ${index !== items.length - 1 ? 'border-b border-solid border-neutral-border' : ''}`}
          >
            {is3DModelUrl(item.image_url) ? (
              <div className="w-12 h-12 rounded-md flex items-center justify-center bg-neutral-100 flex-shrink-0">
                <span style={{ fontSize: '20px' }}>🧊</span>
              </div>
            ) : (
              <img
                src={item.thumbnail_url || item.image_url || '/placeholder-nft.png'}
                alt={item.name || 'NFT'}
                className="w-12 h-12 rounded-md object-cover"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-body-bold font-body-bold text-default-font truncate">
                {item.name || `NFT #${item.nft_token_id.slice(-8)}`}
              </p>
              {item.collection_name && (
                <p className="text-caption font-caption text-subtext-color truncate">
                  {item.collection_name}
                </p>
              )}
            </div>
            <span className="text-body-bold font-body-bold text-default-font whitespace-nowrap">
              {Number(item.price_xrp).toFixed(2)} XRP
            </span>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2 p-3 bg-neutral-50 rounded-md">
        <div className="flex justify-between">
          <span className="text-body font-body text-subtext-color">Subtotal</span>
          <span className="text-body font-body text-default-font">{totals.subtotal.toFixed(2)} XRP</span>
        </div>
        {totals.feeBreakdown.map((entry) => (
          <div key={entry.marketplace} className="flex justify-between">
            <span className="text-caption font-caption text-subtext-color">
              {entry.marketplace} fee ({entry.feePercentage}%)
            </span>
            <span className="text-caption font-caption text-default-font">
              ~{entry.feeAmount.toFixed(2)} XRP
            </span>
          </div>
        ))}
        <div className="flex justify-between pt-2 border-t border-solid border-neutral-border">
          <span className="text-body-bold font-body-bold text-default-font">Total</span>
          <span className="text-heading-3 font-heading-3 text-brand-600">{totals.total.toFixed(2)} XRP</span>
        </div>
      </div>

      <div className="flex gap-2 mobile:flex-col-reverse">
        <Button
          variant="neutral-secondary"
          onClick={onCancel}
          className="flex-1 mobile:w-full"
        >
          Cancel
        </Button>
        <Button
          variant="brand-primary"
          onClick={onStartPurchase}
          className="flex-1 mobile:w-full"
        >
          Start Checkout
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Purchasing Content — live status list
// ============================================================================

interface PurchasingContentProps {
  checkoutItems: CheckoutItemState[];
  isClientSigning: boolean;
  onCancelRemaining: () => void;
  onResolveClientItem: ResolveClientItem;
}

function PurchasingContent({ checkoutItems, isClientSigning, onCancelRemaining, onResolveClientItem }: PurchasingContentProps) {
  const totalCount = checkoutItems.length;
  const resolvedCount = checkoutItems.filter(
    (i) => i.status === 'signed' || i.status === 'rejected' || i.status === 'expired' || i.status === 'error'
  ).length;
  const signedCount = checkoutItems.filter((i) => i.status === 'signed').length;
  const pendingCount = totalCount - resolvedCount;
  const progressPercent = (resolvedCount / totalCount) * 100;

  // Client-signing (joey-wc): sign one item at a time over the WC session. Mount a
  // single SigningStepMonitor for the first pending item with a created payload.
  const activeClientItem = isClientSigning
    ? checkoutItems.find((i) => i.status === 'pending' && i.payload)
    : undefined;

  return (
    <div className="flex flex-col gap-4">
      {/* Progress */}
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <span className="text-body-bold font-body-bold text-default-font">
            {signedCount} of {totalCount} signed
          </span>
          {pendingCount > 0 && (
            <span className="text-caption font-caption text-subtext-color">
              {pendingCount} pending
            </span>
          )}
        </div>
        <Progress value={progressPercent} />
      </div>

      {/* Info */}
      <div className="p-3 bg-brand-50 rounded-md">
        <p className="text-body font-body text-brand-700 text-center">
          {isClientSigning
            ? 'Approve each transaction in your wallet, one at a time'
            : 'Open your Xaman wallet to approve each transaction'}
        </p>
      </div>

      {/* Client-signing: one sequential monitor for the active pending item (renders nothing) */}
      {activeClientItem && activeClientItem.payload && (
        <SigningStepMonitor
          key={activeClientItem.payload.id}
          payload={activeClientItem.payload}
          onSigned={() => onResolveClientItem(activeClientItem.item.nft_token_id, 'signed')}
          onRejected={() => onResolveClientItem(activeClientItem.item.nft_token_id, 'rejected', 'Transaction rejected')}
          onExpired={() => onResolveClientItem(activeClientItem.item.nft_token_id, 'expired')}
          onError={(msg) => onResolveClientItem(activeClientItem.item.nft_token_id, 'error', msg)}
        />
      )}

      {/* Item list with statuses */}
      <div className="max-h-[300px] overflow-y-auto border border-solid border-neutral-border rounded-md mobile:max-h-[250px]">
        {checkoutItems.map((ci, index) => (
          <div
            key={ci.item.nft_token_id}
            className={`flex items-center gap-3 p-3 ${index !== checkoutItems.length - 1 ? 'border-b border-solid border-neutral-border' : ''}`}
          >
            {/* Status icon */}
            <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
              <CheckoutStatusIcon status={ci.status} />
            </div>

            {/* NFT info */}
            {is3DModelUrl(ci.item.image_url) ? (
              <div className="w-10 h-10 rounded-md flex items-center justify-center bg-neutral-100 flex-shrink-0">
                <span style={{ fontSize: '16px' }}>🧊</span>
              </div>
            ) : (
              <img
                src={ci.item.thumbnail_url || ci.item.image_url || '/placeholder-nft.png'}
                alt={ci.item.name || 'NFT'}
                className="w-10 h-10 rounded-md object-cover flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-body-bold font-body-bold text-default-font truncate">
                {ci.item.name || `NFT #${ci.item.nft_token_id.slice(-8)}`}
              </p>
              <p className="text-caption font-caption text-subtext-color">
                <CheckoutStatusLabel status={ci.status} error={ci.error} />
              </p>
            </div>

            {/* Price */}
            <span className="text-caption font-caption text-default-font whitespace-nowrap flex-shrink-0">
              {Number(ci.item.price_xrp).toFixed(2)} XRP
            </span>
          </div>
        ))}
      </div>

      {/* Cancel button */}
      {pendingCount > 0 && (
        <Button
          variant="neutral-secondary"
          onClick={onCancelRemaining}
          className="w-full"
        >
          Cancel Remaining ({pendingCount} left)
        </Button>
      )}
    </div>
  );
}

function CheckoutStatusIcon({ status }: { status: CheckoutItemState['status'] }) {
  switch (status) {
    case 'creating':
      return <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />;
    case 'pending':
      return <FeatherClock className="w-5 h-5 text-brand-600" />;
    case 'signed':
      return <FeatherCheck className="w-5 h-5 text-success-600" />;
    case 'rejected':
    case 'expired':
    case 'error':
      return <FeatherX className="w-5 h-5 text-error-600" />;
    default:
      return <FeatherClock className="w-5 h-5 text-subtext-color" />;
  }
}

function CheckoutStatusLabel({ status, error }: { status: CheckoutItemState['status']; error?: string }) {
  switch (status) {
    case 'creating':
      return <>Sending to Xaman...</>;
    case 'pending':
      return <>Waiting for signature</>;
    case 'signed':
      return <span className="text-success-600">Purchased</span>;
    case 'rejected':
      return <span className="text-error-600">{error || 'Rejected'}</span>;
    case 'expired':
      return <span className="text-error-600">Expired</span>;
    case 'error':
      return <span className="text-error-600">{error || 'Failed'}</span>;
    default:
      return <>Pending</>;
  }
}

// ============================================================================
// Complete Content
// ============================================================================

interface CompleteContentProps {
  completedPurchases: string[];
  failedPurchases: CartPurchaseFailure[];
  onComplete: () => void;
}

function CompleteContent({ completedPurchases, failedPurchases, onComplete }: CompleteContentProps) {
  const hasSuccesses = completedPurchases.length > 0;
  const hasFailures = failedPurchases.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-3 py-4">
        {hasSuccesses && !hasFailures && (
          <>
            <div className="w-16 h-16 rounded-full bg-success-50 flex items-center justify-center">
              <FeatherCheck className="w-8 h-8 text-success-600" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              All purchases completed!
            </p>
            <p className="text-body font-body text-subtext-color">
              Successfully purchased {completedPurchases.length} NFT{completedPurchases.length !== 1 ? 's' : ''}
            </p>
          </>
        )}

        {hasSuccesses && hasFailures && (
          <>
            <div className="w-16 h-16 rounded-full bg-warning-50 flex items-center justify-center">
              <FeatherAlertCircle className="w-8 h-8 text-warning-600" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              Checkout partially completed
            </p>
            <p className="text-body font-body text-subtext-color">
              {completedPurchases.length} purchased, {failedPurchases.length} failed
            </p>
          </>
        )}

        {!hasSuccesses && hasFailures && (
          <>
            <div className="w-16 h-16 rounded-full bg-error-50 flex items-center justify-center">
              <FeatherX className="w-8 h-8 text-error-600" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              Checkout failed
            </p>
            <p className="text-body font-body text-subtext-color">
              No NFTs were purchased
            </p>
          </>
        )}

        {!hasSuccesses && !hasFailures && (
          <>
            <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center">
              <FeatherX className="w-8 h-8 text-subtext-color" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              Checkout cancelled
            </p>
            <p className="text-body font-body text-subtext-color">
              No NFTs were purchased
            </p>
          </>
        )}
      </div>

      {hasFailures && (
        <div className="flex flex-col gap-2">
          <p className="text-body-bold font-body-bold text-error-600">
            Failed purchases:
          </p>
          <div className="max-h-[150px] overflow-y-auto border border-solid border-error-200 rounded-md bg-error-50">
            {failedPurchases.map((failure, index) => (
              <div
                key={failure.tokenId}
                className={`p-2 ${index !== failedPurchases.length - 1 ? 'border-b border-solid border-error-200' : ''}`}
              >
                <p className="text-body-bold font-body-bold text-error-700 truncate">
                  {failure.name}
                </p>
                <p className="text-caption font-caption text-error-600">
                  {failure.error}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button
        variant="brand-primary"
        onClick={onComplete}
        className="w-full"
      >
        Done
      </Button>
    </div>
  );
}
